import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { createSoftwareAppStructuredData, SOURCEDIN_SOFTWARE_DATA } from '@/components/structured-data/software-app'
import { 
  Target, 
  TrendingUp, 
  Users, 
  Shield, 
  ArrowRight, 
  CheckCircle,
  Star,
  Zap
} from 'lucide-react'

export function Vendors() {
  const pageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "For Vendors - RFP Marketplace",
    "description": "Join SourcedIn's RFP marketplace as a vendor. Find qualified opportunities, win more business, and grow your revenue with our intelligent matching platform."
  }

  // Create SoftwareApplication structured data
  const softwareAppData = createSoftwareAppStructuredData(SOURCEDIN_SOFTWARE_DATA)

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="For Vendors - Win More Business | SourcedIn RFP Marketplace"
        description="Join thousands of vendors finding qualified RFP opportunities on SourcedIn. Get matched with buyers, submit winning proposals, and grow your business with our AI-powered marketplace."
        keywords="RFP vendors, business opportunities, vendor marketplace, win RFPs, proposal platform, B2B vendor network, supplier opportunities"
        canonical="https://sourcedin.ai/vendors"
        structuredData={[pageData, softwareAppData]}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Win More Business as a 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600"> Vendor</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Connect with qualified buyers, get matched with perfect opportunities, 
            and grow your revenue through SourcedIn's intelligent RFP marketplace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" className="text-lg px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
                Join as Vendor <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                Browse RFPs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Vendors Choose SourcedIn
            </h2>
            <p className="text-xl text-gray-600">
              The marketplace that helps you win more and grow faster
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Perfect Matches</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Our AI matches you with RFPs that perfectly align with your expertise, 
                  increasing your win rate by up to 300%.
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Skills-based matching</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Location optimization</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Grow Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Vendors on our platform see average revenue growth of 200% 
                  within their first year of joining.
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Higher-value projects</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Recurring opportunities</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">Quality Buyers</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Connect with pre-qualified buyers who have budget, authority, 
                  and genuine need for your services.
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Verified companies</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Budget transparency</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Stats */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Vendor Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real results from vendors like you
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">200%</div>
              <div className="text-gray-600">Average Revenue Growth</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">85%</div>
              <div className="text-gray-600">Win Rate Improvement</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">50K+</div>
              <div className="text-gray-600">Active Vendors</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">$2M</div>
              <div className="text-gray-600">Average Deal Size</div>
            </div>
          </div>

          <Card className="border-0 shadow-lg p-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-2xl font-light text-gray-700 mb-6 italic">
                "SourcedIn transformed our business. We went from struggling to find clients 
                to having a steady pipeline of high-quality projects. Our revenue tripled in 18 months."
              </blockquote>
              <div className="font-semibold text-lg">David Rodriguez</div>
              <div className="text-gray-600">CEO, Strategic Consulting Group</div>
            </div>
          </Card>
        </div>
      </section>

      {/* How It Works for Vendors */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How It Works for Vendors
            </h2>
            <p className="text-xl text-gray-600">
              Start winning more business in just 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-2xl font-bold mb-4">Create Your Profile</h3>
              <p className="text-gray-600 leading-relaxed">
                Build a compelling vendor profile showcasing your expertise, portfolio, 
                and client testimonials. Our optimization tools help you stand out.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-2xl font-bold mb-4">Get Matched</h3>
              <p className="text-gray-600 leading-relaxed">
                Receive personalized RFP recommendations based on your skills and preferences. 
                Our AI ensures you only see relevant, high-quality opportunities.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-2xl font-bold mb-4">Win & Grow</h3>
              <p className="text-gray-600 leading-relaxed">
                Submit winning proposals using our templates and guidance. 
                Build relationships with buyers and grow your business consistently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Spotlight Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Showcase Your Business in Our 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Vendor Spotlight</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Get discovered by thousands of buyers browsing our organized vendor directory. 
                Create a professional profile that showcases your expertise, portfolio, and client testimonials.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Professional vendor profiles with SEO-friendly URLs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Organized by industry for easy buyer discovery</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Portfolio showcase and client testimonial display</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Enhanced search visibility and buyer engagement</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/vendor-spotlight">
                  <Button size="lg" variant="outline" className="text-lg px-6 py-3 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
                    Browse Vendor Directory <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link href="/vendor-profile">
                  <Button size="lg" className="text-lg px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                    Create Your Profile
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="border-0 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-white">TechSolutions Inc.</CardTitle>
                      <p className="text-blue-100 text-sm">Software Development • Verified</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">4.9 (23 reviews)</span>
                    </div>
                    <p className="text-gray-600 text-sm">
                      "Leading software development company specializing in enterprise solutions, 
                      mobile apps, and cloud infrastructure..."
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">React</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Node.js</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">AWS</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full opacity-60"></div>
              <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-green-400 rounded-full opacity-40"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Tools */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Vendor Success Tools
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to win more business
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Proposal Builder</h3>
                  <p className="text-gray-600 mb-4">
                    Create compelling proposals with our templates, best practice guides, 
                    and AI-powered content suggestions.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Professional templates</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Content optimization</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Trust & Credibility</h3>
                  <p className="text-gray-600 mb-4">
                    Build buyer confidence with verified credentials, client testimonials, 
                    and performance badges.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Verified credentials</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Performance ratings</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing for Vendors */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Vendor Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Start free, upgrade when you're ready to win more
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-200 p-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                <div className="text-4xl font-bold text-gray-900 mb-4">$0<span className="text-lg font-normal text-gray-600">/month</span></div>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Browse unlimited RFPs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Submit 3 proposals/month</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Basic profile</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">Get Started Free</Button>
              </div>
            </Card>

            <Card className="border-2 border-green-500 p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
                <div className="text-4xl font-bold text-gray-900 mb-4">$199<span className="text-lg font-normal text-gray-600">/month</span></div>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Everything in Free</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Unlimited proposals</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>AI-powered matching</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Advanced analytics</span>
                  </div>
                </div>
                <Button className="w-full bg-green-600 hover:bg-green-700">Upgrade to Pro</Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Win More Business?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of successful vendors growing their business on SourcedIn.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Join as Vendor <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-green-600">
                Browse Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
